import React from 'react';
import { ArrowRight, Building2, Users, LineChart, Shield, BookOpen, Brain, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ServiceCorporate = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Hero Section - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] mb-6 md:mb-8 text-sm md:text-base">
            Solution Entreprise
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">
            Formez et Développez
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              Vos Talents
            </span>
          </h1>
          <p className="text-base md:text-xl text-white/70 mb-8 md:mb-12">
            Une plateforme complète de formation pour gérer et suivre le développement 
            des compétences de vos collaborateurs
          </p>
          
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="w-full md:w-auto px-6 py-3 md:px-8 md:py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all flex items-center gap-2 justify-center"
            >
              Demander une démo
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => navigate('/subscription')}
              className="w-full md:w-auto px-6 py-3 md:px-8 md:py-4 rounded-lg border border-green-500/50 hover:bg-[#017D7E]/10 text-white font-medium transition-all"
            >
              Voir les tarifs
            </button>
          </div>
        </div>
      </div>

      {/* Features Grid - Responsive */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">
                Une Solution Complète pour la
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                  {" "}Formation Entreprise
                </span>
              </h2>
              <p className="text-base md:text-xl text-white/70">
                Tous les outils nécessaires pour gérer efficacement la formation de vos équipes
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <FeatureCard
                icon={Building2}
                title="Portail Entreprise"
                description="Espace dédié et personnalisé"
                features={[
                  "Dashboard personnalisable",
                  "Gestion des accès",
                  "Branding aux couleurs de l'entreprise"
                ]}
              />
              
              <FeatureCard
                icon={Users}
                title="Gestion des Utilisateurs"
                description="Administration simplifiée des apprenants"
                features={[
                  "Import en masse des utilisateurs",
                  "Gestion des groupes",
                  "Attribution des formations"
                ]}
              />
              
              <FeatureCard
                icon={LineChart}
                title="Suivi & Reporting"
                description="Analyse détaillée des progrès"
                features={[
                  "Tableaux de bord analytiques",
                  "Rapports personnalisés",
                  "Export des données"
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section - Responsive */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl font-bold mb-8 md:mb-12">
                  Des bénéfices concrets
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                    pour votre entreprise
                  </span>
                </h2>
                
                <div className="space-y-6 md:space-y-8">
                  <BenefitItem
                    icon={Shield}
                    title="Conformité & Qualité"
                    description="Assurez la conformité réglementaire et maintenez des standards de qualité élevés"
                  />
                  
                  <BenefitItem
                    icon={BookOpen}
                    title="Montée en Compétences"
                    description="Développez les compétences de vos équipes de manière continue et mesurable"
                  />
                  
                  <BenefitItem
                    icon={Brain}
                    title="Innovation & Performance"
                    description="Favorisez l'innovation et améliorez la performance globale de l'entreprise"
                  />
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl border border-white/10 p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8">Statistiques Clés</h3>
                <div className="space-y-6">
                  <StatItem
                    value="85%"
                    label="Taux de completion moyen des formations"
                  />
                  <StatItem
                    value="92%"
                    label="Satisfaction des apprenants"
                  />
                  <StatItem
                    value="3x"
                    label="Retour sur investissement moyen"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plans Section - Responsive */}
      <div className="py-12 md:py-24 bg-gradient-to-b from-transparent to-black/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">
                Des Plans Adaptés à
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                  {" "}Votre Structure
                </span>
              </h2>
              <p className="text-base md:text-xl text-white/70">
                Solutions flexibles pour entreprises de toutes tailles
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <PlanCard
                title="Startup"
                subtitle="Pour les petites équipes"
                
                
                features={[
                  "Jusqu'à 10 utilisateurs",
                  "Accès au catalogue complet",
                  "Support par email",
                  "Rapports basiques"
                ]}
              />
              
              <PlanCard
                title="Business"
                subtitle="Pour les entreprises en croissance"
               
               
                popular={true}
                features={[
                  "Jusqu'à 50 utilisateurs",
                  "Formations personnalisées",
                  "Support prioritaire",
                  "Rapports avancés",
                  "API Access"
                ]}
              />
              
              <PlanCard
                title="Enterprise"
                subtitle="Pour les grands groupes"
                price="Sur mesure"
                features={[
                  "Utilisateurs illimités",
                  "Solutions sur mesure",
                  "Account manager dédié",
                  "SLA garantis",
                  "Intégration complète"
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composants utilitaires adaptés pour mobile
const FeatureCard = ({ icon: Icon, title, description, features }) => (
  <div className="bg-white/5 rounded-xl p-6 md:p-8 border border-white/10">
    <div className="w-12 h-12 rounded-lg bg-[#017D7E]/10 flex items-center justify-center mb-4 md:mb-6">
      <Icon className="w-6 h-6 text-[#017D7E]" />
    </div>
    <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">{title}</h3>
    <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6">{description}</p>
    <ul className="space-y-2 md:space-y-3">
      {features.map((feature, index) => (
        <li key={index} className="flex items-center gap-2 text-sm md:text-base text-white/70">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
          {feature}
        </li>
      ))}
    </ul>
  </div>
);

const BenefitItem = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col md:flex-row gap-4 items-center md:items-start text-center md:text-left">
    <div className="w-12 h-12 rounded-lg bg-[#017D7E]/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-[#017D7E]" />
    </div>
    <div>
      <h3 className="text-lg md:text-xl font-bold mb-2">{title}</h3>
      <p className="text-sm md:text-base text-white/70">{description}</p>
    </div>
  </div>
);

const StatItem = ({ value, label }) => (
  <div className="text-center md:text-left">
    <div className="text-2xl md:text-4xl font-bold text-[#017D7E] mb-1 md:mb-2">{value}</div>
    <div className="text-sm md:text-base text-white/70">{label}</div>
  </div>
);

const PlanCard = ({ title, subtitle, price, period, features, popular = false }) => (
  <div className={`relative bg-white/5 rounded-xl p-6 md:p-8 border ${popular ? 'border-green-500' : 'border-white/10'}`}>
    {popular && (
      <div className="absolute top-0 right-0 bg-[#017D7E] text-white text-sm font-semibold px-3 py-1 rounded-tr-xl rounded-bl-xl">
        Populaire
      </div>
    )}
    <h3 className="text-xl md:text-2xl font-bold mb-2">{title}</h3>
    <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6">{subtitle}</p>
    <div className="mb-4 md:mb-6">
      <span className="text-2xl md:text-4xl font-bold">{price}</span>
      <span className="text-sm md:text-base text-white/70">{period}</span>
    </div>
    <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
      {features.map((feature, index) => (
        <li key={index} className="flex items-center gap-2 text-sm md:text-base text-white/70">
          <Award className="w-4 h-4 md:w-5 md:h-5 text-[#017D7E] flex-shrink-0" />
          {feature}
        </li>
      ))}
    </ul>
    <button className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all text-sm md:text-base">
      Nous contacter
    </button>
  </div>
);

export default ServiceCorporate;